import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"

/**
 * Incident link controller for Problem Management.
 *
 * data-controller="support--problem-link"
 * data-support--problem-link-search-url-value="/support/tickets"
 * data-support--problem-link-problem-id-value="uuid"
 * data-support--problem-link-link-url-value="/support/problems/uuid/link_incidents"
 */
export default class extends Controller {
  static targets = [
    "modal",
    "searchInput",
    "results",
    "selectedList",
    "submitBtn",
    "submitContainer",
    "form",
    "ticketIds",
  ]

  static values = {
    searchUrl: String,
    problemId: String,
    linkUrl: String,
  }

  connect() {
    this._searchTimeout = null
    this._selectedTickets = new Map()
  }

  openModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.remove("hidden")
      requestAnimationFrame(() => {
        if (this.hasSearchInputTarget) this.searchInputTarget.focus()
      })
    }
  }

  closeModal() {
    if (this.hasModalTarget) {
      this.modalTarget.classList.add("hidden")
      this._resetState()
    }
  }

  _resetState() {
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ""
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.add("hidden")
      this.resultsTarget.replaceChildren()
    }
    if (this.hasSelectedListTarget) {
      this.selectedListTarget.classList.add("hidden")
      this.selectedListTarget.replaceChildren()
    }
    if (this.hasSubmitContainerTarget)
      this.submitContainerTarget.classList.add("hidden")
    if (this.hasTicketIdsTarget) this.ticketIdsTarget.replaceChildren()
    this._selectedTickets.clear()
  }

  search() {
    const query = this.searchInputTarget.value.trim()
    if (this._searchTimeout) clearTimeout(this._searchTimeout)

    if (query.length < 2) {
      this.resultsTarget.classList.add("hidden")
      this.resultsTarget.replaceChildren()
      return
    }

    this._searchTimeout = setTimeout(() => this._performSearch(query), 300)
  }

  async _performSearch(query) {
    try {
      // responseKind: "json" sets the Accept header; TicketsController#search
      // always renders JSON, so no `.json` suffix is needed (and dropping it
      // keeps this resilient if the route is later format-constrained).
      const url = `${this.searchUrlValue}?q=${encodeURIComponent(query)}`
      const response = await get(url, { responseKind: "json" })
      if (!response.ok) return

      const data = await response.json
      // /support/tickets/search renders a flat JSON array; tolerate a wrapped
      // { tickets: [...] } / { problems: [...] } shape too.
      const tickets = Array.isArray(data)
        ? data
        : data.tickets || data.problems || []
      this._renderResults(tickets)
    } catch (error) {
      // UX is unchanged (user can retry), but leave a breadcrumb so a
      // network/parse/5xx failure is triageable in console / Sentry instead
      // of silently showing "No results".
      console.warn("[support--problem-link] incident search failed:", error)
    }
  }

  _renderResults(tickets) {
    const filtered = tickets.filter((t) => !this._selectedTickets.has(t.id))
    this.resultsTarget.replaceChildren()

    if (filtered.length === 0) {
      const p = document.createElement("p")
      p.className = "px-3 py-2 text-sm text-muted-foreground"
      p.textContent = "No tickets found"
      this.resultsTarget.appendChild(p)
      this.resultsTarget.classList.remove("hidden")
      return
    }

    filtered.forEach((t) => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.dataset.action = "click->support--problem-link#selectTicket"
      btn.dataset.ticketId = t.id
      btn.dataset.ticketNumber = t.ticketNumber || t.ticket_number || ""
      btn.dataset.ticketSubject = t.subject || t.title || ""
      btn.className =
        "w-full flex items-center gap-2 px-3 py-2 text-sm hover:bg-muted transition-colors cursor-pointer text-left"

      const numberSpan = document.createElement("span")
      numberSpan.className = "font-mono text-xs text-muted-foreground shrink-0"
      numberSpan.textContent = t.ticketNumber || t.ticket_number || ""

      const subjectSpan = document.createElement("span")
      subjectSpan.className = "truncate text-foreground"
      subjectSpan.textContent = t.subject || t.title || ""

      btn.appendChild(numberSpan)
      btn.appendChild(subjectSpan)
      this.resultsTarget.appendChild(btn)
    })

    this.resultsTarget.classList.remove("hidden")
  }

  selectTicket(event) {
    const btn = event.currentTarget
    const id = btn.dataset.ticketId
    const number = btn.dataset.ticketNumber
    const subject = btn.dataset.ticketSubject

    this._selectedTickets.set(id, { number, subject })
    this._updateSelectedUI()
    this.resultsTarget.classList.add("hidden")
    this.searchInputTarget.value = ""
  }

  removeTicket(event) {
    const id = event.currentTarget.dataset.ticketId
    this._selectedTickets.delete(id)
    this._updateSelectedUI()
  }

  _updateSelectedUI() {
    this.selectedListTarget.replaceChildren()
    this.ticketIdsTarget.replaceChildren()

    if (this._selectedTickets.size === 0) {
      this.selectedListTarget.classList.add("hidden")
      this.submitContainerTarget.classList.add("hidden")
      return
    }

    this.selectedListTarget.classList.remove("hidden")
    this.submitContainerTarget.classList.remove("hidden")

    this._selectedTickets.forEach((t, id) => {
      const chip = document.createElement("span")
      chip.className =
        "inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-md bg-muted border border-border text-foreground"

      const label = document.createTextNode(t.number)
      chip.appendChild(label)

      const removeBtn = document.createElement("button")
      removeBtn.type = "button"
      removeBtn.dataset.action = "click->support--problem-link#removeTicket"
      removeBtn.dataset.ticketId = id
      removeBtn.className = "text-muted-foreground hover:text-foreground"
      removeBtn.innerHTML =
        '<svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>'
      chip.appendChild(removeBtn)

      this.selectedListTarget.appendChild(chip)

      const hidden = document.createElement("input")
      hidden.type = "hidden"
      hidden.name = "ticket_ids[]"
      hidden.value = id
      this.ticketIdsTarget.appendChild(hidden)
    })
  }

  submit() {
    if (this._selectedTickets.size === 0) return
    this.formTarget.requestSubmit()
  }
}
